import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchTechniqueByIdAction, updateTechniqueAction } from '../../../utils/redux/techniques/techniquesAction';
import { fetchAllTechniqueCategories } from '../../../utils/redux/techniqueCategory/techniqueCategoryActions';
import { Container, Row, Col, Card, Badge, Button, Form } from 'react-bootstrap';
import { BsPencilSquare, BsCheck, BsX } from 'react-icons/bs';
// import './TechniqueDetails.css';
import '../CSS/commondetails.css';

const TechniqueDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { selectedTechnique, isLoading } = useSelector((state) => state.techniques);
    const { techniqueCategories } = useSelector((state) => state.techniqueCategory);

    // Edit states
    const [editMode, setEditMode] = useState({
        title: false,
        basicInfo: false,
        links: false,
        description: false,
        summary: false,
        keywords: false,
        category: false
    });

    // Form states
    const [formData, setFormData] = useState({});
    const [selectedCategories, setSelectedCategories] = useState([]);

    useEffect(() => {
        if (id) {
            dispatch(fetchTechniqueByIdAction(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(fetchAllTechniqueCategories());
    }, [dispatch]);

    useEffect(() => {
        if (selectedTechnique) {
            setFormData(selectedTechnique);
            setSelectedCategories(selectedTechnique.categories?.map(cat => cat._id) || []);
        }
    }, [selectedTechnique]);

    const formatDate = (dateString) => {
        if (!dateString) return "No Date";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
        });
    };

    const handleEdit = (field) => {
        setEditMode({ ...editMode, [field]: true });
    };

    const handleCancel = (field) => {
        setEditMode({ ...editMode, [field]: false });
        setFormData({ ...formData, [field]: selectedTechnique[field] });
    };

    const handleSave = async (field) => {
        try {
            let valueToSave = formData[field];

            // Handle array fields (keywords)
            if (field === 'keywords') {
                valueToSave = typeof valueToSave === 'string'
                    ? valueToSave.split(',').map(item => item.trim()).filter(Boolean)
                    : valueToSave;
            }

            if (field === 'categories') {
                valueToSave = selectedCategories;
            }

            const response = await dispatch(updateTechniqueAction(id, {
                [field]: valueToSave
            }));

            if (response?.success) {
                setEditMode({ ...editMode, [field]: false });
                setFormData(prev => ({
                    ...prev,
                    [field]: valueToSave
                }));
                if (field === 'categories') {
                    setFormData(prev => ({
                        ...prev,
                        categories: response?.technique?.categories
                    }));
                }
            }
        } catch (error) {
            console.error('Error updating technique:', error);
        }
    };

    const EditButtons = ({ field }) => (
        <div className="edit-buttons">
            {!editMode[field] ? (
                <Button variant="link" size="sm" onClick={() => handleEdit(field)}>
                    <BsPencilSquare />
                </Button>
            ) : (
                <>
                    <Button variant="link" className="text-success" size="sm" onClick={() => handleSave(field)}>
                        <BsCheck />
                    </Button>
                    <Button variant="link" className="text-danger" size="sm" onClick={() => handleCancel(field)}>
                        <BsX />
                    </Button>
                </>
            )}
        </div>
    );

    const renderEditableField = (field, content, inputType = 'text') => (
        <div className="editable-field">
            {editMode[field] ? (
                inputType === 'textarea' ? (
                    <Form.Control
                        as="textarea"
                        rows={10}
                        className="large-textarea"
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    />
                ) : (
                    <Form.Control
                        type={inputType}
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    />
                )
            ) : (
                content
            )}
            <EditButtons field={field} />
        </div>
    );

    const renderEditableArray = (field, items, placeholder) => (
        <div className="editable-field">
            {editMode[field] ? (
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={`Enter ${field} (comma separated)`}
                    value={typeof formData[field] === 'string'
                        ? formData[field]
                        : (formData[field]?.join(', ') || '')}
                    onChange={(e) => {
                        setFormData({
                            ...formData,
                            [field]: e.target.value
                        });
                    }}
                    className="tags-textarea"
                />
            ) : (
                <div className="tags-container">
                    {Array.isArray(items) && items.length > 0 ? (
                        items.map((item, index) => (
                            <Badge
                                key={index}
                                bg="secondary"
                                className="tag-badge"
                            >
                                {item}
                            </Badge>
                        ))
                    ) : (
                        <span className="text-muted">{placeholder}</span>
                    )}
                </div>
            )}
            <EditButtons field={field} />
        </div>
    );

    if (isLoading || !selectedTechnique) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <Container className="py-4">
            <Card className="talk-details-card">
                <Card.Body>
                    {/* Title Section */}
                    <div className="title-section mb-4">
                        {renderEditableField('title',
                            <h2>{formData.title || "No title available"}</h2>
                        )}
                    </div>

                    <Row className="mb-4">
                        {/* Basic Information Column */}
                        <Col md={6}>
                            <div className="detail-section">
                                <h5 className="section-title">Basic Information</h5>
                                <div className="info-content">
                                    {renderEditableField('durationHhmmss',
                                        <div className="detail-item">
                                            <span className="label">Duration:</span>
                                            <span>{formData.durationHhmmss || "Duration not specified"}</span>
                                        </div>
                                    )}
                                    {editMode.categories ? (
                                        <div className="editable-field">
                                            <Form.Group>
                                                <Form.Label>Categories:</Form.Label>
                                                {techniqueCategories?.map(cat => (
                                                    <Form.Check
                                                        key={cat._id}
                                                        type="checkbox"
                                                        label={cat.name}
                                                        checked={selectedCategories.includes(cat._id)}
                                                        onChange={e => {
                                                            if (e.target.checked) {
                                                                setSelectedCategories([...selectedCategories, cat._id]);
                                                            } else {
                                                                setSelectedCategories(selectedCategories.filter(id => id !== cat._id));
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Form.Group>
                                            <EditButtons field="categories" />
                                        </div>
                                    ) : (
                                        <div className="editable-field">
                                            <div className="detail-item">
                                                <span className="label">Categories:</span>
                                                <span>
                                                    {formData.categories?.length > 0
                                                        ? formData.categories.map(cat => cat?.name).join(', ')
                                                        : "No categories assigned"}
                                                </span>
                                            </div>
                                            <EditButtons field="categories" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>

                        {/* Links Column */}
                        <Col md={6}>
                            <div className="detail-section">
                                <h5 className="section-title">Links</h5>
                                <div className="info-content">
                                    {renderEditableField('techniqueLink',
                                        <div className="detail-item">
                                            <span className="label">Technique Link:</span>
                                            {formData.techniqueLink ? (
                                                <a href={formData.techniqueLink} target="_blank" rel="noopener noreferrer">
                                                    View Technique
                                                </a>
                                            ) : (
                                                <span className="text-muted">No technique link available</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Description Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Description</h5>
                        <div className="content-section">
                            {renderEditableField('description',
                                <div className="content-text formatted-text">
                                    {formData.description ?
                                        formData.description.split('\n').map((paragraph, index) => (
                                            <p key={index}>{paragraph}</p>
                                        ))
                                        : "No description added yet"}
                                </div>,
                                'textarea'
                            )}
                        </div>
                    </div>

                    {/* Summary Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Details Manual</h5>
                        <div className="content-section">
                            {renderEditableField('summary',
                                <div className="content-text summary-background formatted-text">
                                    {formData.summary ?
                                        formData.summary.split('\n').map((paragraph, index) => (
                                            <p key={index}>{paragraph}</p>
                                        ))
                                        : "No Details added yet"}
                                </div>,
                                'textarea'
                            )}
                            {/* <div className="mt-3">
                                <strong>Summary Status:</strong>{" "}
                                {formData.summaryExist ? (
                                    <span className="text-success">Completed</span>
                                ) : (
                                    <span className="text-warning">Not Added</span>
                                )}
                            </div> */}
                        </div>
                    </div>

                    {/* Keywords Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Keywords</h5>
                        {renderEditableArray('keywords', formData.keywords, "No keywords added yet")}
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default TechniqueDetails;